<template>
  <q-card-section class="q-pt-none">
    <!-- Start Time -->
    <div class="row q-gutter-sm q-my-sm">
      <div class="col">
        <!-- Types -->
        <q-select
          filled
          ref="typeRef"
          v-if="typeof typeOptions !== 'undefined'"
          :reactive-rules="true"
          :rules="[(val) => !!val || formLabel('requiredGeneric')]"
          v-model="typeOption"
          :options="typeOptions"
          color="accent"
          :label="formLabel('type')"
          behavior="menu"
        >
          <template #selected-item="{ opt }">
            <q-item dense v-if="opt">{{ formOption(opt) }}</q-item>
          </template>
          <template #option="{ index: optionIndex, opt }">
            <q-item clickable v-close-popup @click="typeOption = typeOptions[optionIndex]">
              {{ formOption(opt) }}
            </q-item>
          </template>
        </q-select>
      </div>
    </div>
    <div v-if="activityType == 'team' && typeOption == 'merch'" class="row q-gutter-sm q-my-sm">
      <div class="col">
        <q-input
          ref="jobRef"
          v-model="job"
          color="accent"
          filled
          type="text"
          :rules="[(val) => !!val || formLabel('requiredGeneric')]"
          mask="######"
          :label="formLabel('job')"
        />
      </div>
      <div class="col">
        <q-input
          ref="waveRef"
          v-model="wave"
          color="accent"
          filled
          type="text"
          :rules="[(val) => !!val || formLabel('requiredGeneric')]"
          mask="###"
          :label="formLabel('wave')"
        />
      </div>
    </div>

    <div class="row q-gutter-sm q-my-sm">
      <div class="col">
        <!-- Location -->
        <q-select
          filled
          ref="locationRef"
          v-if="typeof locationOptions !== 'undefined'"
          :reactive-rules="true"
          :rules="[(val) => !!val || formLabel('requiredGeneric')]"
          v-model="locationOption"
          :options="locationOptions"
          color="accent"
          :label="formLabel('location')"
          behavior="menu"
        >
          <template #selected-item="{ opt }">
            <q-item dense v-if="opt">{{ formOption(opt) }}</q-item>
          </template>
          <template #option="{ index: optionIndex, opt }">
            <q-item clickable v-close-popup @click="locationOption = locationOptions[optionIndex]">
              {{ formOption(opt) }}
            </q-item>
          </template>
        </q-select>
      </div>
    </div>

    <div class="row q-gutter-sm q-my-sm">
      <div class="col">
        <q-input
          ref="startRef"
          :model-value="
            useFormatDate(start).time({
              hour: 'numeric',
              minute: 'numeric',
              hourCycle: 'h23',
            })
          "
          :rules="[
            (val) => isValidDate(val) || '',
            (val) => rules.beforeNow(toDate(val, date)) || formLabel('notBeforeNow'),
            (val) => rules.beforeEnd(toDate(val, date)) || formLabel('startOverlapEnd'),
            (val) => rules.notBetweenTimers(toDate(val, date), index) || formLabel('betweenActivities'),
            (val) =>
              rules.notOverlap(toDate(val, date), 'start', index) || formLabel('overlapActivities'),
            (val) =>
              useChildTimerRules().sumChildrenNotGreaterThanParent(
                { newEnd: end, newStart: toDate(val, date) as Date },
                timer
              ) || formLabel('childrenSum'),
          ]"
          @update:model-value="(val) => (isValidDate(val) ? updateStart(toDate(val, date) as Date) : undefined)"
          color="accent"
          filled
          type="time"
          :label="formLabel('startTime')"
        />
      </div>
    </div>

    <!-- End Time -->
    <div
      class="row q-gutter-sm q-my-sm"
      v-if="(!editing && (endState || insert)) || (editing && endState)"
    >
      <div class="col">
        <q-input
          ref="endRef"
          :model-value="
            useFormatDate(end).time({
              hour: 'numeric',
              minute: 'numeric',
              hourCycle: 'h23',
            })
          "
          @update:model-value="(val) => (isValidDate(val) ? updateEnd(toDate(val, date) as Date) : undefined)"
          :rules="[
            (val) => isValidDate(val),
            (val) => rules.beforeNow(toDate(val, date)) || formLabel('notBeforeNow'),
            (val) => rules.afterStart(toDate(val, date)) || formLabel('endOverlapStart'),
            (val) => rules.notOverlap(toDate(val, date), 'end', index) || formLabel('overlapActivities'),
            (val) => rules.notBetweenTimers(toDate(val, date), index) || formLabel('betweenActivities'),
            (val) =>
              useChildTimerRules().sumChildrenNotGreaterThanParent(
                { newStart: start, newEnd: toDate(val, date) as Date },
                timer
              ) || formLabel('childrenSum'),
          ]"
          color="accent"
          filled
          type="time"
          :label="formLabel('endTime')"
        />
      </div>
    </div>

    <template v-if="hasChildTimer && typeof index === 'number'">
      <q-btn flat @click="showChildModal = true" :label="formLabel('updateLastTask')" />
      <FormChildTimer v-model="showChildModal" :timer="timer" :parent-index="index" />
    </template>

    <!-- Duration -->
    <div class="row q-gutter-sm q-my-sm" v-if="!editing && (endState || insert)">
      <div class="col">
        <q-input
          filled
          bg-color="white"
          color="accent"
          :model-value="duration"
          readonly
          :label="formLabel('duration')"
        />
      </div>
    </div>
  </q-card-section>

  <!-- Actions -->
  <q-card-actions align="right" class="text-primary">
    <q-btn
      flat
      v-if="!endState && !editing"
      @click="close"
      :label="formLabel('close')"
      v-close-popup
    />
    <q-btn
      v-if="!endState && !editing"
      @click="actionAdd(activityType)"
      color="primary"
      :label="formLabel('start')"
      v-close-popup="
        !!(
          rules.beforeNow(start) &&
          (typeof locationOptions === 'undefined' ||
            (typeof locationOptions !== 'undefined' && typeof locationOption !== 'undefined'))
        ) || false
      "
    />
    <!-- Not an insert save -->
    <template v-if="endState && !insert">
      <q-btn-dropdown
        color="primary"
        split
        @click="save(activityType)"
        :label="formLabel('stopSave')"
      >
        <q-list>
          <q-item clickable @click="removeActivity" v-close-popup>
            <q-item-section>
              <q-item-label>{{ formLabel('delete') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
    <!-- Insert save -->
    <template v-else-if="endState && insert && !editing">
      <q-item clickable v-close-popup="2">
        <q-item-section>
          <q-item-label>{{ formLabel('cancel') }}</q-item-label>
        </q-item-section>
      </q-item>

      <q-btn color="primary" split @click="insertActivity" :label="formLabel('saveNewActivity')">
        <!-- <q-list> </q-list> -->
      </q-btn>
    </template>

    <!-- Editing -->
    <template v-else-if="editing">
      <q-item clickable v-close-popup="2">
        <q-item-section>
          <q-item-label>{{ formLabel('cancel') }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-btn
        v-if="activityType == 'store'"
        color="primary"
        @click="editActivity"
        :label="`${formLabel('save')} ${activityLabel(activityType)}`"
      />

      <q-btn-dropdown
        v-if="activityType != 'store'"
        color="primary"
        split
        @click="editActivity"
        :label="`${formLabel('save')} ${activityLabel(activityType)}`"
      >
        <q-list>
          <q-item clickable @click="promptRemoveActivity" v-close-popup>
            <q-item-section>
              <q-item-label>{{ formLabel('delete') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
  </q-card-actions>
  <ModalPromptDelete v-model="modalPromptDelete" @confirm-delete="removeActivity" />
</template>
<script lang="ts">
import { storeToRefs } from 'pinia'
import {
  computed,
  defineComponent,
  inject,
  onBeforeMount,
  onBeforeUpdate,
  PropType,
  Ref,
  ref,
} from 'vue'
import { useFormatDate } from '../composables/formatDate'
import { useFormatTime } from '../composables/formatTime'
import { useStartEndRules } from '../composables/startEndRules'
import { useTimers } from '../store/timers'
import { Timer } from '../types/timers'
import { useI18n } from 'vue-i18n'
import { isValidDate, toDate, stripTime } from '../utils'
import { useUserStore } from '../store/useUserStore'
import ModalPromptDelete from './ModalPromptDelete.vue'
import { useChildTimerRules } from '../composables/useChildTimerRules'
import FormChildTimer from './FormChildTimer.vue'

export default defineComponent({
  components: { ModalPromptDelete, FormChildTimer },
  emits: ['started', 'cancel', 'close'],
  props: {
    /** The type of activity that will be added */
    activityType: {
      required: true,
      type: String as PropType<Timer['type']>,
    },
    /** Used when a parent timer is ended */
    endState: {
      required: false,
      type: Boolean,
      default: false,
    },
    insert: {
      required: false,
      type: Boolean,
      default: false,
    },
    editing: {
      required: false,
      type: Boolean,
      default: false,
    },
    insertTimes: {
      required: false,
      type: Object,
      default: undefined,
    },
    index: {
      required: false,
      type: Number,
      default: undefined,
    },
    insertData: {
      required: false,
      type: Object,
      default: undefined,
    },
    timer: {
      required: false,
      type: Object as PropType<Timer>,
      default: null,
    },
  },
  setup(props, { emit }) {
    //translations
    const { t } = useI18n({ useScope: 'global' })
    const formLabel = (key: string) => t(`form.${key}`)
    const formOption = (key: string) => t(`form.options.${key}`)
    const activityLabel = (key: string) => t(`activities.${key}`)

    const date = inject('date', ref(new Date()))
    const start = ref(new Date())
    stripTime(start, date.value)

    const startRef = ref()
    const typeRef = ref()
    const job = ref()
    const jobRef = ref()
    const wave = ref()
    const waveRef = ref()
    const typeOption = ref()
    const typeOptions = computed(() => {
      if (props.activityType === 'break') {
        return user.breakOptions.value
      }

      if (props.activityType === 'training') {
        return ['atStore', 'virtualOnline', 'retailMerchCoach']
      }

      if (props.activityType === 'material') {
        return ['core', 'merchandising', 'transfer']
      }

      if (props.activityType === 'team') {
        return ['instore', 'merch', 'cart']
      }

      if (props.activityType === 'other') {
        return [
          'operationSupport',
          'recruitingDuties',
          'cartFraming',
          'unscheduledTasks',
          ...user.regionSpecificActivities.value('other'),
        ]
      }

      return undefined
    })
    const locationRef = ref()
    const locationOptions = ref()
    const locationOption = ref()
    const user = storeToRefs(useUserStore())
    const setOptions = () => {
      typeOption.value = undefined
      locationOptions.value = ['home', 'warehouse', 'store', 'other']

      if (props.activityType === 'store') {
        locationOptions.value = undefined
      }
      if (props.activityType === 'break') {
        typeOption.value = typeOptions.value![0]
      }
      if (props.activityType === 'training') {
        typeOption.value = 'virtualOnline'
      }
      if (props.activityType === 'material') {
        typeOption.value = 'core'
      }
      if (props.activityType === 'team') {
        typeOption.value = 'instore'
      }
      if (props.activityType === 'other') {
      }
    }
    onBeforeMount(() => {
      setOptions()
      if (props.insertData) {
        locationOption.value = props.insertData.location
        typeOption.value = props.insertData.subtype
        if (props.activityType == 'team' && props.insertData.subtype == 'merch') {
          job.value = props.insertData.job_id
          wave.value = props.insertData.wave_number
        }
      }
      if (props.endState === true && props.insert !== true) {
        end.value = new Date()
        stripTime(end, date.value)

        start.value = getActiveParentTimer.value!.start_time
        if (getActiveParentTimer.value) {
          locationOption.value = getActiveParentTimer.value.data.location
          typeOption.value = getActiveParentTimer.value.data.subtype
          if (
            getActiveParentTimer.value.type === 'team' &&
            getActiveParentTimer.value.data.subtype == 'merch'
          ) {
            job.value = getActiveParentTimer.value.data.job_id
            wave.value = getActiveParentTimer.value.data.wave_number
          }
          if (getActiveParentTimer.value.type === 'break') {
            typeOption.value = getActiveParentTimer.value.data.code
          }
        }
      } else if (props.insert === true && props.insertTimes) {
        start.value = props.insertTimes.start
        stripTime(start, date.value)

        //if editing while timer is running dont set these
        if (props.insertTimes.end) {
          end.value = props.insertTimes.end
          stripTime(end, date.value)
        }
      }
    })
    onBeforeUpdate(() => {
      setOptions()
      if (props.insertData) {
        locationOption.value = props.insertData.location
        typeOption.value = props.insertData.subtype
        if (props.activityType == 'team' && props.insertData.subtype == 'merch') {
          job.value = props.insertData.job_id
          wave.value = props.insertData.wave_number
        }
      }
    })
    const end = ref()
    const endRef = ref()
    const duration = computed(() => {
      if (end.value && start.value) {
        return useFormatTime(end.value.getTime() - start.value.getTime(), {
          format: 'hh:mm',
          initTime: 'ms',
        })
      } else return 0
    })

    //logic for starting/stopping wrapped for form
    const {
      addParentTimer,
      startBreak,
      endBreak,
      stopActiveParentTimer,
      editParentStartEnd,
      editParentData,
      removeParent,
      sortTimers,
    } = useTimers()
    const { getAllParentTimers, getActiveParentTimer } = storeToRefs(useTimers())
    const validate = () => {
      if (typeRef.value) {
        typeRef.value.validate()
        if (typeRef.value.hasError) return false
      }
      if (jobRef.value) {
        jobRef.value.validate()
        if (jobRef.value.hasError) return false
      }
      if (waveRef.value) {
        waveRef.value.validate()
        if (waveRef.value.hasError) return false
      }
      if (locationRef.value) {
        locationRef.value.validate()
        if (locationRef.value.hasError) return false
      }
      if (startRef.value) {
        startRef.value.validate()
        if (startRef.value.hasError) return false
      }
      if (endRef.value) {
        endRef.value.validate()
        if (endRef.value.hasError) return false
      }

      return true
    }
    const actionAdd = (type: Timer['type']) => {
      if (validate()) {
        emit('started')
        stripTime(start, date.value)
        if (type === 'break') {
          return startBreak({
            code: typeOption.value,
            date: start.value,
            data: { subtype: typeOption.value, location: locationOption.value },
          })
        } else {
          return void addParentTimer(
            {
              type: type,
              data: {
                subtype: typeOption.value,
                location: locationOption.value,
                ...(job.value && { job_id: job.value }),
                ...(wave.value && { wave_number: wave.value }),
              },
            },
            start.value
          )
        }
      }
    }
    const save = (type: Timer['type']) => {
      if (validate()) {
        console.log({ FPA_DATE: date.value, start: start.value })
        stripTime(start, date.value)
        stripTime(end, date.value)
        editParentData(getAllParentTimers.value.length - 1, {
          subtype: typeOption.value,
          location: locationOption.value,
          ...(job.value && { job_id: job.value }),
          ...(wave.value && { wave_number: wave.value }),
        })
        if (type === 'break') endBreak({ date: end.value })
        else stopActiveParentTimer({ date: end.value })
        editParentStartEnd(
          { start_time: start.value, end_time: end.value },
          getAllParentTimers.value.length - 1
        )
        close()
      }
    }
    const { insertNewTimer } = useTimers()
    const insertActivity = () => {
      if (validate()) {
        stripTime(start, date.value)
        void insertNewTimer({
          start_time: start.value,
          end_time: end.value,
          type: props.activityType,
          data: {
            subtype: typeOption.value,
            location: locationOption.value,
            ...(job.value && { job_id: job.value }),
            ...(wave.value && { wave_number: wave.value }),
          },
        })
        sortTimers()
      }
    }
    const editActivity = () => {
      if (validate()) {
        stripTime(start, date.value)
        stripTime(end, date.value)
        editParentData(props.index!, {
          subtype: typeOption.value,
          location: locationOption.value,
          job_id: job.value,
          wave_number: wave.value,
        })
        editParentStartEnd({ start_time: start.value, end_time: end.value }, props.index!)
        sortTimers()
        close()
      }
    }
    const resetForm = () => {
      end.value = undefined
    }
    const removeActivity = () => {
      removeParent(props.index!)
      resetForm()
      emit('cancel')
    }
    const close = () => {
      resetForm()
      emit('close')
    }

    //rules
    const rules = useStartEndRules({ start, end })

    //utils
    const updateTimer =
      (timerRef: Ref<Date>) =>
      (date: Date): void => {
        timerRef.value = date
      }
    const updateStart = updateTimer(start)
    const updateEnd = updateTimer(end)

    const modalPromptDelete = ref(false)
    const promptRemoveActivity = () => {
      modalPromptDelete.value = true
    }

    const hasChildTimer = computed(() => {
      return (
        !!props.timer &&
        !!props.timer.children &&
        props.timer.children.length &&
        props.timer.children[props.timer.children.length - 1].end_time
      )
    })
    const showChildModal = ref(false)
    return {
      date,

      formLabel,
      formOption,
      activityLabel,
      //initial state
      start,
      startRef,
      typeRef,
      typeOption,
      typeOptions,
      locationRef,
      locationOption,
      locationOptions,
      useFormatDate,
      useFormatTime,
      job,
      jobRef,
      wave,
      waveRef,

      //end state
      end,
      endRef,
      duration,

      //action
      actionAdd,
      save,
      insertActivity,
      editActivity,
      close,
      removeActivity,

      //rules
      rules,
      isValidDate,
      useChildTimerRules,

      //utils
      toDate,
      updateStart,
      updateEnd,

      modalPromptDelete,
      promptRemoveActivity,

      //child
      hasChildTimer,
      showChildModal,
    }
  },
})
</script>
